import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { removeSubmissionMemberApi } from "./submissionStorage";

const BORDER = "#e2e8f0";
const MUTED = "#64748b";
const MESSAGE_MAX = 2000;
const REASON_MAX = 200;
const NOTE_MAX = 500;

const CHANNELS = {
  email: {
    label: "Email",
    sent: "Goodbye email sent",
    failed: "Goodbye email not sent",
  },
  inApp: {
    label: "In-app",
    sent: "In-app notification sent",
    failed: "In-app notification not sent",
  },
};

const defaultMessage = (firstName, journeyName) => {
  const who = firstName || "there";
  const journey = journeyName || "this journey";
  return `Hi ${who}, you've been removed from ${journey}. If you have any questions, please contact your coach.`;
};

const getFirstName = (submission = {}) => {
  if (submission.firstName) return String(submission.firstName).trim();
  const name = String(submission.memberName || "").trim();
  if (!name || name === "Unknown" || name.includes("@")) return "";
  return name.split(/\s+/)[0];
};

const ChannelOption = ({ label, checked, disabled, hint, onChange }) => (
  <Box>
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
        />
      }
      label={<Typography sx={{ fontSize: 13.5 }}>{label}</Typography>}
    />
    {hint ? (
      <Typography sx={{ fontSize: 12, color: MUTED, ml: 4, mt: -0.75 }}>
        {hint}
      </Typography>
    ) : null}
  </Box>
);

/**
 * Coach/admin drops a person out of the journey and optionally sends a goodbye.
 */
export default function RemoveMemberCard({
  submission,
  onRemoved,
  defaultOpen = false,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const emailAvailable = Boolean(submission?.email);
  const inAppAvailable = Boolean(submission?.memberId);
  const firstName = getFirstName(submission || {});
  const journeyName = submission?.trackerName || "";

  const [open, setOpen] = useState(Boolean(defaultOpen));
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const [emailChecked, setEmailChecked] = useState(true);
  const [inAppChecked, setInAppChecked] = useState(true);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setOpen(Boolean(defaultOpen));
    setReason("");
    setNote("");
    setMessage(defaultMessage(firstName, journeyName));
    setEmailChecked(true);
    setInAppChecked(true);
  }, [submission?.id, firstName, journeyName, defaultOpen]);

  const wantEmail = emailAvailable && emailChecked;
  const wantInApp = inAppAvailable && inAppChecked;
  const wantsNotify = wantEmail || wantInApp;
  const messageOk = !wantsNotify || Boolean(message.trim());
  const canRemove =
    Boolean(submission?.id) && messageOk && !removing;

  const notifyResults = (result) => {
    if (!result.results) {
      enqueueSnackbar(result.message || "Member removed", {
        variant: "success",
      });
      return;
    }
    [
      ["email", wantEmail],
      ["inApp", wantInApp],
    ].forEach(([key, requested]) => {
      if (!requested) return;
      const outcome = result.results[key];
      if (outcome?.sent) {
        enqueueSnackbar(CHANNELS[key].sent, { variant: "success" });
        return;
      }
      enqueueSnackbar(
        outcome?.error
          ? `${CHANNELS[key].failed}: ${outcome.error}`
          : CHANNELS[key].failed,
        { variant: "warning" }
      );
    });
  };

  const handleRemove = async () => {
    if (!canRemove) return;
    setRemoving(true);
    const result = await removeSubmissionMemberApi(submission.id, {
      reason: reason.trim(),
      note: note.trim(),
      message: wantsNotify ? message.trim() : "",
      notifyEmail: wantEmail,
      notifyInApp: wantInApp,
    });
    setRemoving(false);
    if (!result.ok) {
      enqueueSnackbar(result.message || "Couldn't remove the member", {
        variant: "error",
      });
      return;
    }
    if (result.alreadyRemoved) {
      enqueueSnackbar("They were already off this journey", {
        variant: "info",
      });
    } else {
      enqueueSnackbar("Member removed from the journey", {
        variant: "success",
      });
      notifyResults(result);
    }
    setOpen(false);
    if (onRemoved) await onRemoved(result.submission);
  };

  if (!open) {
    return (
      <Box
        sx={{
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          bgcolor: "#fff",
          mb: 2.5,
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          flexWrap: "wrap",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
            Drop out of journey
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
            Marks them Dropped Out, stops reminders and automation emails, and
            can send a goodbye.
          </Typography>
        </Box>
        <button
          type="button"
          className="small-contained-outlined-button"
          onClick={() => setOpen(true)}
        >
          Drop out…
        </button>
      </Box>
    );
  }

  return (
    <Box
      id="drop-out"
      sx={{
        border: `1px solid #fecaca`,
        borderRadius: "12px",
        bgcolor: "#fff",
        mb: 2.5,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 2.5 },
          py: 1.75,
          borderBottom: `1px solid #fecaca`,
          bgcolor: "#fef2f2",
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
          Drop out of journey
        </Typography>
        <Typography sx={{ mt: 0.35, fontSize: 13, color: MUTED }}>
          This stops their check-in reminders and automation emails. Optionally
          send a goodbye by email and/or in-app notification.
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Reason (optional)"
          value={reason}
          disabled={removing}
          placeholder="e.g. Dropped out, not engaging"
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
          inputProps={{ maxLength: REASON_MAX }}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={2}
          label="Internal note (optional)"
          value={note}
          disabled={removing}
          placeholder="Only visible to coaches"
          onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
          inputProps={{ maxLength: NOTE_MAX }}
          sx={{ mb: 1.5 }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          label="Message to member"
          value={message}
          disabled={removing || !wantsNotify}
          placeholder="Goodbye message…"
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          inputProps={{ maxLength: MESSAGE_MAX }}
          helperText={
            wantsNotify
              ? `${message.length}/${MESSAGE_MAX}`
              : "Turn on email or in-app to send a goodbye"
          }
          FormHelperTextProps={{
            sx: { textAlign: wantsNotify ? "right" : "left", mr: 0 },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
            mt: 1,
          }}
        >
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <ChannelOption
              label="Send email"
              checked={wantEmail}
              disabled={!emailAvailable || removing}
              hint={emailAvailable ? "" : "No email on file"}
              onChange={setEmailChecked}
            />
            <ChannelOption
              label="Send in-app notification"
              checked={wantInApp}
              disabled={!inAppAvailable || removing}
              hint={inAppAvailable ? "" : "No portal account"}
              onChange={setInAppChecked}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
            <button
              type="button"
              className="small-contained-outlined-button"
              disabled={removing}
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="small-contained-button"
              disabled={!canRemove}
              onClick={handleRemove}
              style={{ backgroundColor: "#dc2626" }}
            >
              {removing ? (
                <Box
                  component="span"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}
                >
                  <CircularProgress size={14} color="inherit" />
                  Removing…
                </Box>
              ) : (
                "Confirm drop out"
              )}
            </button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
